import gql from 'graphql-tag';
import { useApolloClient } from '@apollo/react-hooks';

// The payout Hasura Actions. Each one takes the driver from the signed-in session, never from
// these variables, and returns the masked account only. Sent as the driver role, so a user
// whose default role is something else (an admin who also drives) still acts as their driver.
const DRIVER_ROLE = { headers: { 'x-hasura-role': 'driver' } };

const PAYOUT_METHOD_FIELDS = `
  hasPayoutMethod
  accountName
  bankName
  accountType
  accountLast4
  status
  createdAt
`;

const GET_MY_PAYOUT_METHOD = gql`
  query driver_wallet_getMyPayoutMethod {
    getMyPayoutMethod {
      ${PAYOUT_METHOD_FIELDS}
    }
  }
`;

const SET_MY_DIRECT_DEPOSIT = gql`
  mutation driver_wallet_setMyDirectDeposit($input: SetMyDirectDepositInput!) {
    setMyDirectDeposit(input: $input) {
      ${PAYOUT_METHOD_FIELDS}
    }
  }
`;

const REMOVE_MY_BANK_ACCOUNT = gql`
  mutation driver_wallet_removeMyBankAccount {
    removeMyBankAccount {
      ${PAYOUT_METHOD_FIELDS}
    }
  }
`;

/** The Action's own message (it is written for the driver), or null. Never the variables. */
const actionMessage = err => {
  const first = err && err.graphQLErrors && err.graphQLErrors[0];
  return first && typeof first.message === 'string' ? first.message : null;
};

//////////////////////// HOOK ////////////////////////

export function useWallet() {
  const client = useApolloClient();

  /** The signed-in driver's payout account, masked. */
  const getPayoutMethod = async () => {
    try {
      const res = await client.query({ query: GET_MY_PAYOUT_METHOD, fetchPolicy: 'network-only', context: DRIVER_ROLE });
      return { success: true, data: res.data.getMyPayoutMethod };
    } catch (err) {
      console.error(`Failed to retrieve payout account`);
      return { success: false, message: actionMessage(err) };
    }
  };

  /** Add or replace the signed-in driver's bank account. */
  const setDirectDeposit = async ({ accountName, accountType, routingNumber, accountNumber }) => {
    try {
      const res = await client.mutate({
        mutation: SET_MY_DIRECT_DEPOSIT,
        variables: { input: { accountName, accountType, routingNumber, accountNumber } },
        context: DRIVER_ROLE,
      });
      return { success: true, data: res.data.setMyDirectDeposit };
    } catch (err) {
      console.error(`Failed to save bank account`);
      return { success: false, message: actionMessage(err) };
    }
  };

  /** Remove the signed-in driver's bank account. */
  const removeBankAccount = async () => {
    try {
      const res = await client.mutate({ mutation: REMOVE_MY_BANK_ACCOUNT, context: DRIVER_ROLE });
      return { success: true, data: res.data.removeMyBankAccount };
    } catch (err) {
      console.error(`Failed to remove bank account`);
      return { success: false, message: actionMessage(err) };
    }
  };

  return {
    getPayoutMethod,
    setDirectDeposit,
    removeBankAccount,
  };
}
